import { useAuthState } from 'react-firebase-hooks/auth';
import { signOut as firebaseSignOut } from 'firebase/auth';
import { clearTenantId } from '@elka/firebase';
import { useTenantClaims } from '@elka/hooks';
import { auth } from '@/lib/firebase/config';

/**
 * Authentication hook for the elka-manager back-office.
 *
 * Authorization is claim-based only: a user is authorized iff they hold a
 * `tenantId` custom claim.
 */
export function useAuth() {
  const [user, loading, error] = useAuthState(auth);
  const claims = useTenantClaims(auth);

  const hasTenantClaim = claims.status === 'ready';
  const tenantId = claims.status === 'ready' ? claims.tenantId : null;
  const role = claims.status === 'ready' ? claims.role : null;
  const superAdmin = claims.status === 'ready' ? claims.superAdmin : false;
  const isUnprovisioned = claims.status === 'unprovisioned';

  const isAuthorized = hasTenantClaim;

  const signOut = async () => {
    try {
      await firebaseSignOut(auth);
    } catch (err) {
      console.error('Error signing out:', err);
      throw err;
    } finally {
      clearTenantId();
      // Full reload is LOAD-BEARING: hooks memoize their queries with
      // useMemo(..., []) and the session tenant lives in a module-level variable,
      // so a SPA navigation would keep streaming the previous tenant's data.
      // A hard reload resets both the React tree and the module state.
      window.location.assign('/login');
    }
  };

  return {
    user,
    isLoading: loading || claims.status === 'loading',
    error: error?.message || null,
    signOut,
    isAuthenticated: !!user,
    isAuthorized,
    /** Authenticated but has no `tenantId` claim yet. */
    isUnprovisioned,
    /** Tenant from custom claims, or null until 'ready'. */
    tenantId,
    /** Office role from custom claims, or null. */
    role,
    /** Backend `superAdmin` custom claim (payslips + audit-log gate). False until claims are 'ready'. */
    superAdmin,
  };
}
